// v2 road bridge head — a battered ashlar abutment: three stepped masonry courses
// narrowing as they rise (the battered profile), pale quoin blocks proud at the
// corners, a dark relieving niche set into the middle of each side face, and round
// corner drums with capstones and ball finials. Authored symmetric on all four
// sides/corners so it reads as the bridge ramp from either span direction. The
// asphalt apron's top sits at y = 0.47. Footprint-native 2x2. Ported 1:1 from
// age-of-mcp's v2/road_bridge_head.ts, using the library's shared `foundationSlab`
// and `scaffold` helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, type Part } from "../../lib";

const STONE    = "#8d8a82";   // ashlar courses
const STONE_DK = "#6f6c64";   // alternate course / capstones
const QUOIN    = "#a09d94";   // proud corner blocks
const NICHE    = "#36342f";   // relieving niches
const ASPHALT  = "#3b3b40";   // the two lanes continuing onto the deck

const WALK_TOP = 0.47;        // the renderer's HEAD_TOP for this type — exact

// The three battered masonry courses (each narrower than the one below).
function courses(upTo: number): Part[] {
  const all: Part[] = [
    { geometry: new THREE.BoxGeometry(1.92, 0.2, 1.92), color: STONE,
      position: [0, 0.2, 0] },
    { geometry: new THREE.BoxGeometry(1.82, 0.1, 1.82), color: STONE_DK,
      position: [0, 0.35, 0] },
    { geometry: new THREE.BoxGeometry(1.74, 0.04, 1.74), color: STONE,
      position: [0, 0.42, 0] },
  ];
  return all.slice(0, upTo);
}

// The asphalt apron — its top IS the walkable deck height.
function apron(): Part {
  return { geometry: new THREE.BoxGeometry(1.68, 0.03, 1.68), color: ASPHALT,
    position: [0, WALK_TOP - 0.015, 0] };
}

// Pale quoin blocks standing proud at the four corners of the lower course.
function quoins(): Part[] {
  const out: Part[] = [];
  for (const x of [-0.9, 0.9]) {
    for (const z of [-0.9, 0.9]) {
      out.push({ geometry: new THREE.BoxGeometry(0.16, 0.22, 0.16), color: QUOIN,
        position: [x, 0.21, z] });
    }
  }
  return out;
}

// A dark relieving niche set into the middle of each of the four side faces.
function niches(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.5, 0.18, 0.04), color: NICHE,
      position: [0, 0.2, 0.965] },
    { geometry: new THREE.BoxGeometry(0.5, 0.18, 0.04), color: NICHE,
      position: [0, 0.2, -0.965] },
    { geometry: new THREE.BoxGeometry(0.04, 0.18, 0.5), color: NICHE,
      position: [0.965, 0.2, 0] },
    { geometry: new THREE.BoxGeometry(0.04, 0.18, 0.5), color: NICHE,
      position: [-0.965, 0.2, 0] },
  ];
}

// Round corner drums with capstones and ball finials — the abutment's railing
// terminals, identical on all four corners so they block neither span axis.
function drums(): Part[] {
  const out: Part[] = [];
  for (const x of [-0.8, 0.8]) {
    for (const z of [-0.8, 0.8]) {
      out.push(
        { geometry: new THREE.CylinderGeometry(0.13, 0.14, 0.6, 6), color: STONE_DK,
          position: [x, 0.3, z] },
        { geometry: new THREE.CylinderGeometry(0.155, 0.155, 0.05, 6), color: STONE,
          position: [x, 0.625, z] },
        { geometry: new THREE.SphereGeometry(0.045, 6, 5), color: STONE_DK,
          position: [x, 0.675, z] },
      );
    }
  }
  return out;
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(2.0, 0.1)];
  if (s === 1) return [foundationSlab(2.0, 0.1), ...courses(1), ...scaffold(1.85, 0.32)];
  if (s === 2) return [foundationSlab(2.0, 0.1), ...courses(3), ...scaffold(1.85, 0.5)];
  return [
    foundationSlab(2.0, 0.1),
    ...courses(3),
    apron(),
    ...quoins(),
    ...niches(),
    ...drums(),
  ];
}

export const roadBridgeHeadV2 = defineModel({
  id: "road-bridge-head-v2",
  tiles: { w: 2, h: 2 },
  batchId: "fable",
  name: "Road bridge head (v2)",
  tags: ["building","road","bridge","head","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly road bridge head.",
  description: "A ramp onto a road bridge; a pair on opposite banks spans a gap with a two-lane deck for vehicles.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [],
});
